/**
 * Execute legacy scripts in global scope — identical semantics to a native
 * `<script>` tag with inline code.
 *
 * Legacy JS files in public/js/ were written to run as standalone
 * <script src="..."> tags, where top-level `var`, `function` declarations
 * become properties of the global (window) object. Webpack's default
 * behavior wraps each module in its own function scope — which breaks
 * cross-file references.
 *
 * Files are imported with the `?legacy` resourceQuery which returns their
 * raw source via webpack's asset/source module type, then injected here
 * as a dynamically-created <script> element. The browser parses and
 * executes the code in a fresh global script execution context — so
 * `function foo() {}` on top level becomes `window.foo`, and `'use strict'`
 * at the top of the file behaves exactly like it did originally.
 *
 * Indirect eval `(0, eval)(code)` was tried first but fails for files with
 * `'use strict'` — strict-mode direct/indirect eval keeps top-level
 * declarations scoped to the eval call, losing them after it returns.
 *
 * @param {...string} codes Raw JS source strings to execute in order.
 */
export function runLegacy(...codes) {
    codes.forEach((code) => {
        const script = document.createElement('script');
        script.textContent = code;
        document.head.appendChild(script);
        script.remove();
    });
}

/**
 * Conditionally execute a legacy script — used to avoid cross-file pollution
 * in multi-controller bundles (e.g. user-account, catalog-pages).
 *
 * Some legacy files register jQuery(document).ready handlers or top-level
 * imperative code that assumes globals injected by inline <script> blocks
 * on a specific page template (e.g. urlLoadMessages, isFee, #editWishForm).
 * When bundled, the file is loaded on every controller that uses the bundle
 * — firing ReferenceErrors on pages where those globals don't exist.
 *
 * Guard runs before injection: if the predicate fails, the script is
 * skipped entirely.
 *
 * @param {string|function} predicate - CSS selector (string) or a function
 *        returning truthy to run. Evaluated synchronously before load.
 * @param {string} code - Raw JS source.
 */
export function runLegacyIf(predicate, code) {
    const shouldRun = typeof predicate === 'string'
        ? !!document.querySelector(predicate)
        : predicate();
    if (shouldRun) {
        runLegacy(code);
    }
}
